import React, { useEffect, useState, useCallback } from 'react';
import {
  Employee,
  PaymentPreview,
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  payEmployee,
} from '../services/employeeService';
import { getAccounts } from '../services/apiService';
import { AccountAutocomplete } from '../components/ui/AccountAutocomplete';

const TYPE_OPTIONS = [
  { value: 'socio', label: 'Sócio (Pró-labore)' },
  { value: 'clt', label: 'CLT' },
  { value: 'pj', label: 'PJ' },
  { value: 'mei', label: 'MEI' },
  { value: 'autonomo', label: 'Autônomo' },
];

const TYPE_BADGES: Record<string, string> = {
  socio: 'bg-purple-100 text-purple-700',
  clt: 'bg-green-100 text-green-700',
  pj: 'bg-blue-100 text-blue-700',
  mei: 'bg-yellow-100 text-yellow-700',
  autonomo: 'bg-orange-100 text-orange-700',
};

function formatCurrency(val: string) {
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatLedgerPosting(p: { account: string; amount: number; commodity: string }) {
  const sign = p.amount < 0 ? '-' : '';
  const abs = Math.abs(p.amount).toFixed(2);
  return `    ${p.account.padEnd(36)}${sign}${p.commodity} ${abs}`;
}

const emptyForm = {
  id: '',
  name: '',
  type: 'pj',
  cpf: '',
  cnpj: '',
  role: '',
  valor_mensal: '',
  conta_despesa: '',
  conta_passivo: '',
};

export default function EmployeesPage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Payment modal state
  const [payModal, setPayModal] = useState<Employee | null>(null);
  const [payDate, setPayDate] = useState('');
  const [payValor, setPayValor] = useState('');
  const [payDescricao, setPayDescricao] = useState('');
  const [payPreview, setPayPreview] = useState<PaymentPreview | null>(null);
  const [payLoading, setPayLoading] = useState(false);

  // Batch modal state
  const [showBatch, setShowBatch] = useState(false);
  const [batchPreviews, setBatchPreviews] = useState<{ emp: Employee; preview: PaymentPreview }[]>([]);
  const [batchDate, setBatchDate] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [emps, accts] = await Promise.all([getEmployees(), getAccounts()]);
      setEmployees(emps);
      setAccounts(accts.accounts);
    } catch {
      setError('Erro ao carregar dados');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (emp: Employee) => {
    setEditingId(emp.id);
    setForm({
      id: emp.id,
      name: emp.name,
      type: emp.type,
      cpf: emp.cpf,
      cnpj: emp.cnpj,
      role: emp.role,
      valor_mensal: emp.valor_mensal,
      conta_despesa: emp.conta_despesa,
      conta_passivo: emp.conta_passivo,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        const { id: _id, ...data } = form;
        await updateEmployee(editingId, data);
      } else {
        await createEmployee({ ...form, id: form.id });
      }
      resetForm();
      load();
      setSuccess(editingId ? 'Funcionário atualizado' : 'Funcionário cadastrado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDeactivate = async (emp: Employee) => {
    if (!window.confirm(`Desativar "${emp.name}"?`)) return;
    try {
      await deleteEmployee(emp.id);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao desativar');
    }
  };

  // --- Payment modal ---
  const openPayModal = (emp: Employee) => {
    const today = new Date().toISOString().slice(0, 10);
    setPayModal(emp);
    setPayDate(today);
    setPayValor(emp.valor_mensal);
    const typeLabels: Record<string, string> = {
      socio: 'Pró-labore', clt: 'Salário', pj: 'Serviço PJ', mei: 'Serviço MEI', autonomo: 'Serviço Autônomo',
    };
    setPayDescricao(`Pagamento ${typeLabels[emp.type] || 'Pagamento'} - ${emp.name}`);
    setPayPreview(null);
    loadPayPreview(emp.id, today);
  };

  const loadPayPreview = async (empId: string, date: string, valor?: string, descricao?: string) => {
    try {
      const res = await payEmployee(empId, { date, valor, descricao });
      if (res.preview) setPayPreview(res.preview);
    } catch {
      // ignore preview errors
    }
  };

  const confirmPay = async () => {
    if (!payModal) return;
    setPayLoading(true);
    try {
      await payEmployee(payModal.id, {
        date: payDate,
        valor: payValor,
        descricao: payDescricao,
        commit: true,
      });
      setPayModal(null);
      setSuccess('Pagamento registrado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao registrar pagamento');
    } finally {
      setPayLoading(false);
    }
  };

  // --- Batch ---
  const openBatch = async () => {
    const today = new Date().toISOString().slice(0, 10);
    setBatchDate(today);
    setBatchLoading(true);
    setShowBatch(true);

    const active = employees.filter(e => e.ativo === 'true');
    const previews: { emp: Employee; preview: PaymentPreview }[] = [];
    for (const emp of active) {
      try {
        const res = await payEmployee(emp.id, { date: today });
        if (res.preview) previews.push({ emp, preview: res.preview });
      } catch {
        // skip
      }
    }
    setBatchPreviews(previews);
    setBatchLoading(false);
  };

  const confirmBatch = async () => {
    setBatchLoading(true);
    try {
      for (const { emp } of batchPreviews) {
        await payEmployee(emp.id, { date: batchDate, commit: true });
      }
      setShowBatch(false);
      setSuccess(`${batchPreviews.length} pagamentos registrados`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro no lote');
    } finally {
      setBatchLoading(false);
    }
  };

  const showDocField = form.type === 'pj' || form.type === 'mei';

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Funcionários e Prestadores</h1>
          <div className="flex gap-2">
            {!showForm && (
              <>
                <button onClick={openBatch} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium">
                  Gerar Folha do Mês
                </button>
                <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">
                  Novo Cadastro
                </button>
              </>
            )}
          </div>
        </div>

        {error && <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 rounded">{error}</div>}
        {success && <div className="mb-4 p-3 text-sm text-green-700 bg-green-50 rounded">{success}</div>}

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-white rounded-lg shadow space-y-3">
            <h2 className="font-semibold text-gray-800">{editingId ? 'Editar Cadastro' : 'Novo Cadastro'}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {!editingId && (
                <input placeholder="ID (slug)" value={form.id} onChange={e => setForm({ ...form, id: e.target.value })} required className="block w-full rounded border px-3 py-2" />
              )}
              <input placeholder="Nome" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="block w-full rounded border px-3 py-2" />

              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="block w-full rounded border px-3 py-2">
                {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              <input placeholder="Cargo / Função" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="block w-full rounded border px-3 py-2" />

              {(form.type === 'clt' || form.type === 'socio' || form.type === 'autonomo') && (
                <input placeholder="CPF" value={form.cpf} onChange={e => setForm({ ...form, cpf: e.target.value })} className="block w-full rounded border px-3 py-2" />
              )}
              {showDocField && (
                <input placeholder="CNPJ" value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} className="block w-full rounded border px-3 py-2" />
              )}

              <input placeholder="Valor Mensal" type="number" step="0.01" value={form.valor_mensal} onChange={e => setForm({ ...form, valor_mensal: e.target.value })} className="block w-full rounded border px-3 py-2" />

              <AccountAutocomplete
                value={form.conta_despesa}
                accounts={accounts}
                onChange={v => setForm({ ...form, conta_despesa: v })}
                placeholder="Conta de Despesa"
                className="block w-full rounded border px-3 py-2"
              />

              <AccountAutocomplete
                value={form.conta_passivo}
                accounts={accounts}
                onChange={v => setForm({ ...form, conta_passivo: v })}
                placeholder="Conta Passivo (obrigação)"
                className="block w-full rounded border px-3 py-2"
              />
            </div>

            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">Salvar</button>
              <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Cancelar</button>
            </div>
          </form>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-sm text-gray-600">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Cargo</th>
                <th className="px-4 py-3">Valor Mensal</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {employees.map(emp => (
                <tr key={emp.id} className="text-sm hover:bg-blue-50 even:bg-gray-100">
                  <td className="px-4 py-3 font-medium">{emp.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${TYPE_BADGES[emp.type] || 'bg-gray-100 text-gray-700'}`}>
                      {emp.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{emp.role}</td>
                  <td className="px-4 py-3">{formatCurrency(emp.valor_mensal)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${emp.ativo === 'true' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {emp.ativo === 'true' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 space-x-2">
                    <button onClick={() => handleEdit(emp)} className="text-blue-600 hover:underline text-sm">Editar</button>
                    {emp.ativo === 'true' && (
                      <button onClick={() => openPayModal(emp)} className="text-green-600 hover:underline text-sm">Pagar</button>
                    )}
                    {emp.ativo === 'true' && (
                      <button onClick={() => handleDeactivate(emp)} className="text-red-600 hover:underline text-sm">Desativar</button>
                    )}
                  </td>
                </tr>
              ))}
              {employees.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Nenhum funcionário cadastrado</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment modal */}
      {payModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4">
            <h2 className="text-lg font-semibold">Pagamento - {payModal.name}</h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Data</label>
                <input type="date" value={payDate} onChange={e => {
                  setPayDate(e.target.value);
                  loadPayPreview(payModal.id, e.target.value, payValor, payDescricao);
                }} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Valor</label>
                <input type="number" step="0.01" value={payValor} onChange={e => {
                  setPayValor(e.target.value);
                  loadPayPreview(payModal.id, payDate, e.target.value, payDescricao);
                }} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Descrição</label>
                <input type="text" value={payDescricao} onChange={e => {
                  setPayDescricao(e.target.value);
                  loadPayPreview(payModal.id, payDate, payValor, e.target.value);
                }} className="w-full border rounded px-3 py-2" />
              </div>
            </div>

            {payPreview && (
              <div>
                <label className="block text-sm text-gray-600 mb-1">Preview do Lançamento</label>
                <pre className="bg-gray-50 p-3 rounded text-xs font-mono overflow-x-auto">
                  {payPreview.date} {payPreview.payee}{'\n'}
                  {payPreview.postings.map(formatLedgerPosting).join('\n')}
                </pre>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setPayModal(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">
                Cancelar
              </button>
              <button onClick={confirmPay} disabled={payLoading} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium disabled:opacity-50">
                {payLoading ? 'Salvando...' : 'Confirmar Pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch modal */}
      {showBatch && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[80vh] overflow-y-auto">
            <h2 className="text-lg font-semibold">Folha do Mês</h2>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Data</label>
              <input type="date" value={batchDate} onChange={e => setBatchDate(e.target.value)} className="w-full border rounded px-3 py-2" />
            </div>

            {batchLoading ? (
              <p className="text-gray-500 text-sm">Gerando previews...</p>
            ) : batchPreviews.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhum funcionário ativo encontrado.</p>
            ) : (
              <div className="space-y-3">
                {batchPreviews.map(({ emp, preview }) => (
                  <div key={emp.id} className="border rounded p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">{emp.name}</span>
                      <span className="text-sm text-gray-500">{formatCurrency(emp.valor_mensal)}</span>
                    </div>
                    <pre className="bg-gray-50 p-2 rounded text-xs font-mono overflow-x-auto">
                      {preview.date} {preview.payee}{'\n'}
                      {preview.postings.map(formatLedgerPosting).join('\n')}
                    </pre>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowBatch(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">
                Cancelar
              </button>
              <button onClick={confirmBatch} disabled={batchLoading || batchPreviews.length === 0} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium disabled:opacity-50">
                {batchLoading ? 'Processando...' : `Confirmar ${batchPreviews.length} Pagamentos`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
